/**
 * DocumentResultCard — one SharePoint search hit (Brief 91).
 *
 * Title opens the preview drawer; matter ref/description opens the matter;
 * three explicit actions: Preview (drawer), Open in SharePoint (new tab),
 * Download. Type icon is derived from the `type` bucket the backend assigns.
 */
import {
  FileText, Mail, FileSpreadsheet, File as FileIcon,
  Eye, ExternalLink, Download, Plus, Check,
} from "lucide-react";

const TYPE_ICON = {
  pdf: FileText,
  word: FileText,
  email: Mail,
  excel: FileSpreadsheet,
  other: FileIcon,
};

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentResultCard({ doc, onPreview, onOpenMatter, onAddToBundle, inBundle }) {
  const Icon = TYPE_ICON[doc.type] || FileIcon;
  const breadcrumb = (doc.breadcrumb || []).join(" / ");

  return (
    <div
      data-testid={`doc-card-${doc.id}`}
      className="rounded-lg border border-navy-border bg-navy-card p-4 hover:border-teal/50 transition-colors"
    >
      <div className="flex items-start gap-3">
        <Icon size={20} className="text-teal shrink-0 mt-0.5" data-testid="doc-type-icon" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              data-testid={`doc-title-${doc.id}`}
              onClick={() => onPreview && onPreview(doc)}
              className="text-left text-sm font-semibold text-white hover:text-teal truncate block min-w-0"
              title={doc.title}
            >
              {doc.title || "(untitled)"}
            </button>
            {doc.matter_ref_match && (
              <span
                data-testid={`doc-exact-badge-${doc.id}`}
                title="Exact matter-ref match"
                className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal/20 text-teal border border-teal"
              >
                Exact match
              </span>
            )}
          </div>

          {(doc.matter_ref || doc.matter_description) && (
            <button
              type="button"
              data-testid={`doc-matter-${doc.id}`}
              onClick={() => onOpenMatter && onOpenMatter(doc)}
              className="mt-0.5 text-xs text-teal hover:underline truncate block w-full text-left"
            >
              {doc.matter_ref}
              {doc.matter_description ? ` — ${doc.matter_description}` : ""}
            </button>
          )}

          {breadcrumb && (
            <div className="mt-1 text-[11px] text-slate-400 truncate" title={breadcrumb}>
              {breadcrumb}
            </div>
          )}

          <div className="mt-1 text-[11px] text-slate-500">
            {formatDate(doc.modified_at)}
            {doc.modified_by ? ` · ${doc.modified_by}` : ""}
            {doc.size_bytes ? ` · ${formatSize(doc.size_bytes)}` : ""}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              data-testid={`doc-action-preview-${doc.id}`}
              onClick={() => onPreview && onPreview(doc)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs
                bg-slate-800 text-slate-200 border border-navy-border hover:bg-slate-700"
            >
              <Eye size={13} /> Preview
            </button>
            {doc.web_url && (
              <a
                data-testid={`doc-action-open-${doc.id}`}
                href={doc.web_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs
                  bg-slate-800 text-slate-200 border border-navy-border hover:bg-slate-700"
              >
                <ExternalLink size={13} /> Open in SharePoint
              </a>
            )}
            {doc.download_url && (
              <a
                data-testid={`doc-action-download-${doc.id}`}
                href={doc.download_url}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs
                  bg-slate-800 text-slate-200 border border-navy-border hover:bg-slate-700"
              >
                <Download size={13} /> Download
              </a>
            )}
            {onAddToBundle && (
              <button
                type="button"
                data-testid={`doc-action-bundle-${doc.id}`}
                onClick={() => onAddToBundle(doc)}
                disabled={inBundle}
                aria-pressed={!!inBundle}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border ${
                  inBundle
                    ? "bg-teal/20 text-teal border-teal cursor-default"
                    : "bg-slate-800 text-slate-200 border-navy-border hover:bg-slate-700"
                }`}
              >
                {inBundle ? <><Check size={13} /> In bundle</> : <><Plus size={13} /> Add to bundle</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
